import { useState, useEffect, useCallback } from "react";
import { CalendarDays, DollarSign, Users, Star, Home, ShoppingCart, Clock, RefreshCw, TrendingUp, Package, AlertTriangle } from "lucide-react";
import api, { formatPrice, getStatusLabel, getStatusColor } from "@/lib/api";
import ShiftSummary from "@/components/admin/ShiftSummary";

const INV_DAYS = 7;

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadData = useCallback(() => {
    api.get("/admin/dashboard").then((r) => {
      setData(r.data);
      setLastUpdate(new Date());
    }).catch((e) => console.error("dashboard load:", e)).finally(() => setLoading(false));
    api.get(`/admin/inventory/movements?days=${INV_DAYS}`).then(r => setInventory(r.data)).catch((e) => console.error("inv movements:", e));
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading) return <div className="text-muted-foreground">Загрузка...</div>;
  if (!data) return <div className="text-muted-foreground">Ошибка загрузки данных</div>;

  const stats = [
    { label: "Бронирований сегодня", value: data.today_bookings, icon: CalendarDays, color: "text-primary" },
    { label: "Выручка (брони)", value: formatPrice(data.today_revenue), icon: DollarSign, color: "text-green-400" },
    { label: "Выручка (заказы)", value: formatPrice(data.today_orders_revenue), icon: ShoppingCart, color: "text-blue-400" },
    { label: "Новые заказы", value: data.new_orders_count, icon: ShoppingCart, color: "text-orange-400" },
    { label: "Всего клиентов", value: data.total_clients, icon: Users, color: "text-cyan-400" },
    { label: "Отзывов на модерации", value: data.pending_reviews, icon: Star, color: "text-yellow-400" },
  ];

  const getHouseName = (h) => h.name_ru || h.name_kz || h.name || "";
  const getCurrentOccupant = (houseId) => data.currently_occupied?.find(o => o.house_id === houseId);
  const getTodayBookings = (houseId) => data.today_bookings_list?.filter(b => b.house_id === houseId) || [];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Дашборд</h1>
        <div className="flex items-center gap-3">
          {lastUpdate && (
            <span className="text-xs text-muted-foreground">
              Обновлено: {lastUpdate.toLocaleTimeString("ru-RU")}
            </span>
          )}
          <button onClick={loadData} className="text-muted-foreground hover:text-primary transition-colors" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Итог смены */}
      <ShiftSummary />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="dashboard-stats">
        {stats.map((s, i) => (
          <div key={i} className="bg-card border border-white/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <s.icon className={`w-4 h-4 ${s.color}`} />
              <span className="text-xs text-muted-foreground">{s.label}</span>
            </div>
            <p className="text-2xl font-serif">{s.value}</p>
          </div>
        ))}
      </div>

      {/* House Map - Real-time Status */}
      <div className="bg-card border border-white/5 p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Home className="w-4 h-4 text-primary" />
          <h2 className="font-sans font-semibold text-sm uppercase tracking-widest">Карта домиков — сейчас</h2>
          <div className="flex gap-3 ml-auto text-xs">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full" /> Свободен</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full" /> Занят</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-500 rounded-full" /> Бронь сегодня</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="houses-map">
          {data.houses?.map((h) => {
            const occupant = getCurrentOccupant(h.id);
            const todayBookings = getTodayBookings(h.id);
            const isOccupied = !!occupant;
            const hasBookings = todayBookings.length > 0;
            return (
              <div
                key={h.id}
                className={`p-3 border rounded-sm transition-all ${
                  isOccupied ? "border-red-500/50 bg-red-900/20" :
                  hasBookings ? "border-yellow-500/50 bg-yellow-900/10" :
                  "border-green-500/50 bg-green-900/10"
                }`}
                data-testid={`house-status-${h.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${isOccupied ? "bg-red-500 animate-pulse" : hasBookings ? "bg-yellow-500" : "bg-green-500"}`} />
                  <span className="font-sans text-sm font-medium">{getHouseName(h)}</span>
                </div>
                <span className="text-xs text-muted-foreground capitalize">{h.house_type}</span>
                {isOccupied && (
                  <div className="mt-1.5 text-xs">
                    <p className="text-red-400">{occupant.client_name}</p>
                    <p className="text-muted-foreground">{occupant.start_hour}:00-{occupant.end_hour}:00</p>
                  </div>
                )}
                {!isOccupied && hasBookings && (
                  <div className="mt-1.5 text-xs text-yellow-400">
                    {todayBookings.length} бронь
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Revenue per House Today */}
      <div className="bg-card border border-white/5 p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h2 className="font-sans font-semibold text-sm uppercase tracking-widest">Доход по домикам сегодня</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="house-revenue-table">
            <thead>
              <tr className="border-b border-white/10 text-muted-foreground text-xs">
                <th className="text-left py-2 font-normal">Домик</th>
                <th className="text-right py-2 font-normal">Брони</th>
                <th className="text-right py-2 font-normal">Заказы</th>
                <th className="text-right py-2 font-normal">Итого</th>
              </tr>
            </thead>
            <tbody>
              {data.houses?.map((h) => {
                const rev = data.house_revenue?.[h.id] || {};
                return (
                  <tr key={h.id} className="border-b border-white/5">
                    <td className="py-2 font-sans">{getHouseName(h)}</td>
                    <td className="py-2 text-right">
                      {rev.bookings_count > 0 && <span className="text-xs text-muted-foreground mr-2">({rev.bookings_count})</span>}
                      <span className="text-green-400">{formatPrice(rev.booking_revenue || 0)}</span>
                    </td>
                    <td className="py-2 text-right">
                      {rev.orders_count > 0 && <span className="text-xs text-muted-foreground mr-2">({rev.orders_count})</span>}
                      <span className="text-blue-400">{formatPrice(rev.order_revenue || 0)}</span>
                    </td>
                    <td className="py-2 text-right font-semibold text-primary">{formatPrice(rev.total || 0)}</td>
                  </tr>
                );
              })}
              <tr className="border-t border-white/10 font-semibold">
                <td className="py-2">Всего</td>
                <td className="py-2 text-right text-green-400">{formatPrice(data.today_revenue)}</td>
                <td className="py-2 text-right text-blue-400">{formatPrice(data.today_orders_revenue)}</td>
                <td className="py-2 text-right text-primary">{formatPrice(data.today_revenue + data.today_orders_revenue)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Inventory Movements — always visible, even when empty */}
      <div className="bg-card border border-white/5 p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Package className="w-4 h-4 text-primary" />
          <h2 className="font-sans font-semibold text-sm uppercase tracking-widest">Бар — расход за {INV_DAYS} дней</h2>
          {inventory?.ingredients?.length > 0 && (
            <span className="text-xs text-muted-foreground ml-2">{inventory.ingredients.length} позиций</span>
          )}
        </div>
        {!inventory ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Загрузка...</p>
        ) : inventory.ingredients.length === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">
            <p>Нет данных. Чтобы здесь появилась статистика:</p>
            <ol className="text-left inline-block mt-2 space-y-1 list-decimal list-inside">
              <li>Добавьте порции на странице <span className="text-primary">Бар</span></li>
              <li>Привяжите их к блюдам через 🧪 в <span className="text-primary">Меню</span></li>
              <li>После заказов здесь появится расход по дням</li>
            </ol>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="inventory-movements-table">
              <thead>
                <tr className="border-b border-white/10 text-muted-foreground text-xs">
                  <th className="text-left py-2 font-normal sticky left-0 bg-card">Ингредиент</th>
                  {inventory.dates.map(d => {
                    const date = new Date(d);
                    return <th key={d} className="text-right py-2 font-normal whitespace-nowrap px-1.5">{date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" })}</th>;
                  })}
                  <th className="text-right py-2 font-normal whitespace-nowrap pl-3">Расход</th>
                  <th className="text-right py-2 font-normal whitespace-nowrap pl-3">Остаток</th>
                </tr>
              </thead>
              <tbody>
                {inventory.ingredients.map(ing => {
                  const isLow = ing.current <= ing.min_quantity;
                  return (
                    <tr key={ing.id} className="border-b border-white/5">
                      <td className="py-2 font-sans sticky left-0 bg-card">{ing.name}</td>
                      {inventory.dates.map(d => (
                        <td key={d} className="py-2 text-right px-1.5 text-muted-foreground">
                          {ing.by_day[d] > 0 ? <span className="text-orange-400">−{ing.by_day[d]}</span> : "—"}
                        </td>
                      ))}
                      <td className="py-2 text-right pl-3 font-semibold text-orange-400">
                        {ing.total_used > 0 ? `−${ing.total_used}` : "—"}
                      </td>
                      <td className={`py-2 text-right pl-3 font-semibold ${isLow ? "text-red-400" : "text-primary"}`}>
                        {ing.current} {ing.unit}
                        {isLow && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[10px] text-muted-foreground mt-3">Учитываются только заказы с настроенной калькуляцией (рецептом).</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Orders */}
        <div className="bg-card border border-white/5 p-5">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingCart className="w-4 h-4 text-orange-400" />
            <h2 className="font-sans font-semibold text-sm uppercase tracking-widest">Активные заказы</h2>
            {data.new_orders_count > 0 && <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full animate-pulse">{data.new_orders_count}</span>}
          </div>
          <div className="space-y-2" data-testid="active-orders">
            {data.active_orders?.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 text-center">Нет активных заказов</p>
            ) : (
              data.active_orders?.slice(0, 6).map((o) => (
                <div key={o.id} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                  <div>
                    <span className="font-sans">{o.house_name_ru || o.house_name_kz || "—"}</span>
                    <span className="text-xs text-muted-foreground ml-2">{o.client_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-primary font-semibold">{formatPrice(o.total_amount)}</span>
                    <span className={`text-xs px-2 py-0.5 ${o.status === "new" ? "bg-orange-900/30 text-orange-400" : "bg-blue-900/30 text-blue-400"}`}>
                      {o.status === "new" ? "Новый" : o.status === "accepted" ? "Принят" : "Готовится"}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="bg-card border border-white/5 p-5">
          <h2 className="font-sans font-semibold text-sm uppercase tracking-widest mb-4">Последние бронирования</h2>
          <div className="space-y-2" data-testid="recent-bookings">
            {data.recent_bookings?.length === 0 ? (
              <p className="text-muted-foreground text-sm">Нет бронирований</p>
            ) : (
              data.recent_bookings?.slice(0, 6).map((b) => (
                <div key={b.id} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                  <div>
                    <span className="font-sans">{b.house_name_ru || b.house_name || "—"}</span>
                    <span className="text-xs text-muted-foreground ml-2">{b.date} {b.start_hour}:00</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{b.client_name}</span>
                    <span className={`text-xs px-2 py-0.5 ${getStatusColor(b.status)}`}>{getStatusLabel(b.status)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-6 bg-card border border-white/5 p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className="text-xs text-muted-foreground">Общая выручка (брони)</span>
            <p className="text-3xl font-serif text-primary">{formatPrice(data.total_revenue)}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Всего бронирований</span>
            <p className="text-3xl font-serif">{data.total_bookings}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Всего заказов</span>
            <p className="text-3xl font-serif">{data.total_orders_count}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 text-center">
        <span className="text-xs text-muted-foreground/50 flex items-center justify-center gap-1">
          <Clock className="w-3 h-3" /> Автообновление каждые 30 сек
        </span>
      </div>
    </div>
  );
}
